import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  convertDateFormatInUtc,
  convertDateToCT,
  downloadDocument,
  downloadImagesAsZip,
} from "../../../utils/util";
import {
  downloadShipmentDoc,
  getInvoiceDetails,
  getShipmentBYItemId,
  getShipmentTrackingBYItemId,
  syncShipmentWithWMS,
  updateShipment,
} from "../../../reducers/shipment.slice";
import { createAxiosShipment } from "../../../utils/request";
import { getUsers } from "../../../utils/getUser";
import Slider from "react-slick";
import {
  ArrowLeft,
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  FilePenLine,
  ImageOff,
  MoreHorizontal,
  X as CloseX,
} from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { generatePDF } from "../../../utils/generateInvoice";
import { uploadPayoutInvoice } from "../../../reducers/payouts.slice";
import { AuthContext } from "../../../context/authContext";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectOption } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { Badge } from "../../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible";
import StatusBadge from "../../../components/shared/StatusBadge";
import useMediaQuery from "../../../components/shared/useMediaQuery";

// WMS damage photos require RSC's own auth header; fetch via shipment axios instance.
const AuthenticatedWmsImage = ({ path, alt, onClick, style: imgStyle }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setObjectUrl(null);
    setFailed(false);

    createAxiosShipment()
      .get(path, { responseType: "blob" })
      .then((response) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(response.data);
        setObjectUrl(createdUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path]);

  if (failed) {
    return (
      <div
        style={{
          ...imgStyle,
          backgroundColor: "#2a2a2a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.25)",
        }}
      >
        <ImageOff size={16} />
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div
        style={{
          ...imgStyle,
          backgroundColor: "#2a2a2a",
          animation: "wmsPulse 1.4s ease-in-out infinite",
        }}
      >
        <style>{`@keyframes wmsPulse { 0%,100%{opacity:.5} 50%{opacity:.9} }`}</style>
      </div>
    );
  }

  return (
    <img
      src={objectUrl}
      alt={alt}
      onClick={onClick}
      style={{ ...imgStyle, objectFit: "cover", cursor: onClick ? "pointer" : "default" }}
    />
  );
};

const mapWmsStatus = (status) => {
  switch (status) {
    case "completed":
      return { wrapperStatus: "RECEIVED", label: "Completed" };
    case "receiving":
    case "in_progress":
      return { wrapperStatus: "IN_TRANSIT", label: "Receiving" };
    case "cancelled":
      return { wrapperStatus: "CANCELLED", label: "Cancelled" };
    case "not_received_yet":
      return { wrapperStatus: "LABEL_PENDING", label: "Not received yet" };
    case "wms_sync_failed":
      return { wrapperStatus: "CANCELLED", label: "Sync failed" };
    default:
      return { wrapperStatus: status, label: status || "Unknown" };
  }
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeKey = (value) => String(value || "").trim().toLowerCase();
const getExpectedComparisonKey = (item) => normalizeKey(item.name || item.item_name || item.product_name || item.item_id);
const getReceivedComparisonKey = (item) => normalizeKey(item.product_name || item.name || item.product_id || item.sku);
const getComparisonMeta = (item) => {
  if (item.upc) return `UPC ${item.upc}`;
  if (item.identifierValue) return `WMS identifier ${item.identifierValue}`;
  return "No product identifier";
};

const getComparisonStatusStyle = (status) => {
  switch (status) {
    case "Matched":
      return { color: "#65e08a", background: "rgba(101,224,138,0.12)", borderColor: "rgba(101,224,138,0.32)" };
    case "Awaiting Sync":
      return { color: "#7dd3fc", background: "rgba(125,211,252,0.12)", borderColor: "rgba(125,211,252,0.32)" };
    case "Damaged":
      return { color: "#ffb86b", background: "rgba(255,184,107,0.13)", borderColor: "rgba(255,184,107,0.34)" };
    default:
      return { color: "#ff8a75", background: "rgba(255,138,117,0.13)", borderColor: "rgba(255,138,117,0.34)" };
  }
};

const getShipmentItems = (boxDetails = []) =>
  (boxDetails || []).flatMap((box) =>
    (box.box_items || []).map((item, index) => ({
      ...item,
      boxId: box.id,
      boxName: box.box_name,
      itemIndex: index,
      expectedQuantity: toNumber(item.quantity),
    }))
  );

const getReceivedItems = (wmsResults = []) =>
  (wmsResults || []).flatMap((result) =>
    (result.items || []).map((item) => ({
      ...item,
      trackingNumber: result.tracking_number,
      warehouseName: result.warehouse_name,
      cleanQuantity: toNumber(item.clean_quantity),
      damagedQuantity: toNumber(item.damaged_quantity),
      receivedQuantity: toNumber(item.total_quantity) || toNumber(item.clean_quantity) + toNumber(item.damaged_quantity),
    }))
  );

const formatDelta = (value) => {
  if (value > 0) return `+${value}`;
  return String(value);
};

const ViewShipmentDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = getUsers();
  const shipmentID = location?.state?.shipmentId;
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [showLoader, setShowLoader] = useState(false);
  const [shipmentData, setShipmentData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [slideIndexes, setSlideIndexes] = useState({});
  const [shipmentType, setShipmentType] = useState("");
  const [openBoxes, setOpenBoxes] = useState({});

  const [updateQuantiyOpen, setUpdateQuantiyOpen] = useState(false);

  const [formData, setFormData] = useState({
    status: "",
    ship_to_address: sessionStorage.getItem("ship_to_address") || "",
    tracking_number: sessionStorage.getItem("trackingNumbers") || "",
  });
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [editedQuantities, setEditedQuantities] = useState({});
  const [selectedBoxItem, setSelectedBoxItem] = useState(null);
  const [quantityError, setQuantityError] = useState("");
  const [isRefresh, setIsRefresh] = useState(false);
  const [boxItem, setBoxItem] = useState(
    JSON.parse(sessionStorage.getItem("updatedQuantities")) || []
  );
  const quantityInputRef = useRef(null);
  const [tempQuantity, setTempQuantity] = useState("");
  const [viewTrackingData, setViewTrackingData] = useState([]);
  const [carrierTrackingLoaded, setCarrierTrackingLoaded] = useState(false);
  const [carrierTrackingLoading, setCarrierTrackingLoading] = useState(false);
  const [wmsSyncResults, setWmsSyncResults] = useState(null);
  const [wmsSyncError, setWmsSyncError] = useState("");
  const [wmsSyncLoading, setWmsSyncLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("comparison");
  const [comparisonFilter, setComparisonFilter] = useState("all");

  const itemsPerPage = 4;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const sliderRefs = useRef({});

  const previous = (boxId) => {
    sliderRefs.current[boxId]?.slickPrev();
  };

  const next = (boxId) => {
    sliderRefs.current[boxId]?.slickNext();
  };

  const { showToast } = useContext(AuthContext);

  const handleDownloadShipmentDoc = (action) => {
    const payload = {
      id: shipmentID,
      docType: action,
    };
    setShowLoader(true);
    dispatch(downloadShipmentDoc(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload?.length === 1) {
        const item = res.payload[0];
        downloadDocument(item?.document_bytes, action, item?.content_type);
      } else if (res?.payload?.length > 1) {
        downloadImagesAsZip(res.payload, action);
      }

      showToast("File downloaded successfully", "success");
      setShowLoader(false);
    });
  };

  useEffect(() => {
    setShowLoader(true);
    dispatch(getShipmentBYItemId(shipmentID)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      setShipmentType(res?.payload?.shipment_type);
      if (res?.payload?.shipment_type === "SHIP_DIRECT") {
        setBoxItem(res?.payload?.box_details[0]?.box_items);
        sessionStorage.setItem(
          "updatedQuantities",
          JSON.stringify(res?.payload?.box_details[0]?.box_items)
        );
      }
      setShipmentData({
        ...res?.payload,
        createdAt: convertDateFormatInUtc(
          res?.payload?.created_at,
          "MMMM D, YYYY"
        ),
        member_name:
          res?.payload?.user_details?.first_name +
          " " +
          res?.payload?.user_details?.last_name,
        displayStatus:
          res?.payload?.status === "LABEL_PENDING"
            ? "Label Pending"
            : res?.payload?.status === "PENDING_SHIPMENT"
              ? "Shipment Pending"
              : res?.payload?.status === "SHIPPED"
                ? "Shipped"
                : res?.payload?.status === "IN_TRANSIT"
                  ? "In-Transit"
                  : res?.payload?.status === "RECEIVED"
                    ? "Received"
                    : res?.payload?.status === "DELAYED"
                      ? "Delayed"
                      : res?.payload?.status === "CANCELLED"
                        ? "Cancelled"
                        : "Unknown Status",
      });
    });
  }, [isRefresh]);

  const getValidStatusTransitions = (currentStatus) => {
    const transitions = {
      LABEL_PENDING: ["CANCELLED"],
      PENDING_SHIPMENT: ["SHIPPED", "CANCELLED"],
      SHIPPED: ["RECEIVED", "IN_TRANSIT", "CANCELLED"],
      IN_TRANSIT: ["RECEIVED", "CANCELLED"],
      RECEIVED: ["CANCELLED"],
      DELAYED: ["CANCELLED"],
      CANCELLED: [],
    };

    return transitions[currentStatus] || [];
  };

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    afterChange: (current, boxId) => {
      setSlideIndexes((prev) => ({
        ...prev,
        [boxId]: current,
      }));
    },
  };

  const ItemCard = ({ item }) => {
    const [copiedField, setCopiedField] = useState(null);
    const [isTextExpanded, setIsTextExpanded] = useState(false);

    const handleCopy = (value) => {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopiedField(value);
          setTimeout(() => setCopiedField(null), 2000);
        })
        .catch((err) => console.error("Failed to copy: ", err));
    };

    const renderCopyableText = (label, value) => (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 8, marginTop: 6, minWidth: 0 }}>
        <span style={{ color: "#8b9296", width: 100, flexShrink: 0, fontSize: 13 }}>{label}:</span>
        <span
          onClick={() => setIsTextExpanded(!isTextExpanded)}
          style={{
            fontSize: 13,
            color: "#fff",
            display: "-webkit-box",
            WebkitLineClamp: isTextExpanded ? "unset" : 3,
            WebkitBoxOrient: "vertical",
            overflow: isTextExpanded ? "visible" : "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
            transition: "all 0.2s ease",
            flex: 1,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </span>
        <button
          onClick={() => handleCopy(value)}
          style={{
            padding: 4,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: copiedField === value ? "#4caf50" : "rgba(255,255,255,0.52)",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Copy size={14} />
        </button>
      </div>
    );

    return (
      <div style={{ padding: "12px 14px", marginTop: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(255,255,255,0.03)" }}>
        <p style={{ fontWeight: 700, color: "#fff", fontSize: 14, margin: "0 0 2px", overflowWrap: "anywhere" }}>{item.name || `SKU ${item.index + 1}`}</p>
        {item.name && <p style={{ color: "#8b9296", fontSize: 11.5, margin: 0 }}>SKU {item.index + 1}</p>}
        {renderCopyableText("UPC", item.upc)}
        {renderCopyableText("Quantity", item.quantity)}
        {renderCopyableText("Order ID", item.order_id)}
        {renderCopyableText("Item Name", item.name)}
      </div>
    );
  };

  const handleUpdateStatus = () => {
    setShowLoader(true);
    const payload = {
      id: shipmentID,
      status: formData.status,
      shipment_type: shipmentType,
    };

    if (!shipmentType) {
      delete payload.shipment_type;
    }
    dispatch(updateShipment(payload)).then(async (res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (formData.status === "SHIPPED") {
        if (!res?.payload?.generate_invoice) {
          setShowLoader(false);
          navigate(-1);
          return;
        }
        await dispatch(getInvoiceDetails({ id: shipmentID })).then(
          async (response) => {
            if (response?.error && Object.keys(response?.error)?.length > 0) {
              setShowLoader(false);
              showToast(response?.error?.message, "error");
              return;
            }

            const formdata = generatePDF(response?.payload);
            const uploadPayload = {
              id: res?.payload?.payout_id,
              document_type: "INVOICE",
              invoice_number: response?.payload?.invoice_number,
            };

            await dispatch(
              uploadPayoutInvoice({ formdata, uploadPayload })
            ).then((uploadResponse) => {
              if (
                uploadResponse?.error &&
                Object.keys(uploadResponse?.error)?.length > 0
              ) {
                setShowLoader(false);
                showToast(uploadResponse?.error?.message, "error");
                return;
              }
            });
          }
        );
      }
      setShipmentData((prev) => ({
        ...prev,
        status: formData.status,
        displayStatus:
          formData.status === "LABEL_PENDING"
            ? "Label Pending"
            : formData.status === "PENDING_SHIPMENT"
              ? "Shipment Pending"
              : formData.status === "SHIPPED"
                ? "Shipped"
                : formData.status === "IN_TRANSIT"
                  ? "In-Transit"
                  : formData.status === "RECEIVED"
                    ? "Received"
                    : formData.status === "DELAYED"
                      ? "Delayed"
                      : formData.status === "CANCELLED"
                        ? "Cancelled"
                        : "Unknown Status",
      }));

      showToast("Status updated successfully", "success");
      setOpenStatusDialog(false);
      setShowLoader(false);
    });
  };

  const EditableItemCard = ({ item, boxId }) => {
    const [copiedField, setCopiedField] = useState(null);

    const handleCopy = (value) => {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopiedField(value);
          setTimeout(() => setCopiedField(null), 2000);
        })
        .catch((err) => console.error("Failed to copy: ", err));
    };

    const renderCopyableText = (label, value) => (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 6, fontSize: 13, minWidth: 0 }}>
        <span style={{ color: "#8b9296", flexShrink: 0 }}>{label}:</span>
        <span style={{ color: "#fff", minWidth: 0, overflowWrap: "anywhere" }}>{value}</span>
        <button
          onClick={() => handleCopy(value)}
          style={{
            padding: 4,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: copiedField === value ? "#4caf50" : "rgba(255,255,255,0.52)",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Copy size={12} />
        </button>
      </div>
    );

    const quantityKey = `${boxId}-${item.index}`;
    const displayedQuantity =
      editedQuantities[quantityKey] !== undefined
        ? editedQuantities[quantityKey]
        : item.quantity;

    const isEdited = editedQuantities[quantityKey] !== undefined;

    return (
      <div style={{ padding: "12px 14px", marginTop: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(255,255,255,0.03)" }}>
        <p style={{ fontWeight: 700, color: "#fff", fontSize: 14, margin: "0 0 2px", overflowWrap: "anywhere" }}>{item.name || `Item ${item.index + 1}`}</p>
        {item.name && <p style={{ color: "#8b9296", fontSize: 11.5, margin: 0 }}>Item {item.index + 1}</p>}
        {renderCopyableText("UPC", item.upc)}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontSize: 13 }}>
          <span style={{ color: "#8b9296" }}>Quantity:</span>
          <span style={{ fontWeight: isEdited ? 700 : 400, color: isEdited ? "#4CAF50" : "#fff" }}>
            {displayedQuantity}
          </span>
          {user.role === "MEMBER" &&
            shipmentData?.status === "PENDING_SHIPMENT" && (
              <button
                onClick={() => openQuantityEditDialog(item, boxId)}
                style={{
                  padding: 4,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.52)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FilePenLine size={14} />
              </button>
            )}
        </div>
        {renderCopyableText("Order ID", item.order_id)}
        {renderCopyableText("Item Name", item.name)}
      </div>
    );
  };

  const openQuantityEditDialog = (item, boxId) => {
    setSelectedBoxItem({
      ...item,
      boxId,
      itemIndex: item.index,
    });

    const key = `${boxId}-${item.index}`;
    const currentValue = editedQuantities[key] || item.quantity;
    setTempQuantity(currentValue.toString());
    setQuantityError("");
    setUpdateQuantiyOpen(true);

    setTimeout(() => {
      if (quantityInputRef.current) {
        quantityInputRef.current.focus();
      }
    }, 100);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setQuantityError("Quantity is required");
      return;
    }

    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) {
      setQuantityError("Please enter a valid number");
      return;
    }

    if (numValue <= 0) {
      setQuantityError("Quantity must be greater than 0");
      return;
    }

    if (!Number.isInteger(numValue)) {
      setQuantityError("Quantity must be a whole number");
      return;
    }

    if (selectedBoxItem && numValue > selectedBoxItem.approved_quantity) {
      setQuantityError(
        `Quantity cannot exceed approved quantity ${selectedBoxItem.approved_quantity} for this ${selectedBoxItem.name}`
      );
      return;
    }

    setQuantityError("");
  };

  const handleConfirmUpdateQuantity = () => {
    if (selectedBoxItem && tempQuantity && !quantityError) {
      const numValue = parseInt(tempQuantity);
      const key = `${selectedBoxItem.boxId}-${selectedBoxItem.itemIndex}`;

      setEditedQuantities((prev) => ({
        ...prev,
        [key]: numValue,
      }));

      const updatedShipmentData = JSON.parse(JSON.stringify(shipmentData));

      const boxIndex = updatedShipmentData.box_details.findIndex(
        (box) => box.id === selectedBoxItem.boxId
      );

      if (boxIndex !== -1) {
        updatedShipmentData.box_details[boxIndex].box_items[
          selectedBoxItem.itemIndex
        ].quantity = numValue;
      }

      setBoxItem(updatedShipmentData.box_details[0]?.box_items);

      sessionStorage.setItem(
        "updatedQuantities",
        JSON.stringify(updatedShipmentData.box_details[0]?.box_items)
      );
    }

    setUpdateQuantiyOpen(false);
  };

  const handleResetQuantities = () => {
    setEditedQuantities({});
    sessionStorage.removeItem("editedQuantity");
    sessionStorage.removeItem("updatedQuantities");
    showToast("All quantity changes have been discarded", "info");
  };

  const shipping_address = [
    "23831 Via Fabricante, Suite 302, Mission Viejo, CA 92691",
    "RSC, 201 Carson Drive, Bear, DE 19701",
  ];

  const handleAddressSelect = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      ship_to_address: value || "",
    }));

    if (!value) {
      sessionStorage.setItem("ship_to_address", "");
      return;
    }

    sessionStorage.setItem("ship_to_address", value);
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isUploading) {
        event.preventDefault();
        event.returnValue = "API is still processing. Please wait.";
      }
    };

    const blockRefresh = (event) => {
      if (
        showLoader &&
        (event.key === "F5" || (event.ctrlKey && event.key === "r"))
      ) {
        event.preventDefault();
        showToast("API is still processing. Please wait.", "warning");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", blockRefresh);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", blockRefresh);
    };
  }, [isUploading]);

  const handleSaveShipment = () => {
    const rawTrackingNumbers = formData.tracking_number
      .split(/[,\n\s]+/)
      .map((num) => num.trim())
      .filter((num) => num.length > 0);

    const uniqueTrackingNumbers = [...new Set(rawTrackingNumbers)];

    const trackingFrequency = {};
    rawTrackingNumbers.forEach((num) => {
      trackingFrequency[num] = (trackingFrequency[num] || 0) + 1;
    });

    const duplicates = Object.keys(trackingFrequency).filter(
      (num) => trackingFrequency[num] > 1
    );

    if (duplicates.length > 0) {
      const duplicatesList =
        duplicates.length <= 3
          ? duplicates.join(", ")
          : `${duplicates.slice(0, 3).join(", ")} and ${duplicates.length - 3} more`;

      setErrorMessage(`Removed duplicate tracking numbers: ${duplicatesList}`);
    }

    if (uniqueTrackingNumbers.length === 0) {
      setErrorMessage("Please enter at least one tracking number.");
      return;
    }

    setShowLoader(true);
    setIsUploading(true);
    const payload = {
      id: shipmentID,
      shipment_type: shipmentType,
      status: "SHIPPED",
      box_items: boxItem,
      tracking_number: uniqueTrackingNumbers,
      ship_to_address: formData.ship_to_address,
    };

    dispatch(updateShipment(payload)).then(async (res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload?.warnings?.length > 0) {
        res.payload.warnings.forEach((warning) => {
          showToast(warning, "warning");
        });
      }
      if (!res?.payload?.generate_invoice) {
        setShowLoader(false);
        navigate(-1);
        return;
      }
      await dispatch(getInvoiceDetails({ id: shipmentID })).then(
        async (response) => {
          if (response?.error && Object.keys(response?.error)?.length > 0) {
            setShowLoader(false);
            showToast(response?.error?.message, "error");
            return;
          }
          const formdata = generatePDF(response?.payload);
          const uploadPayload = {
            id: res?.payload?.payout_id,
            document_type: "INVOICE",
            invoice_number: response?.payload?.invoice_number,
          };

          await dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then(
            (uploadResponse) => {
              if (
                uploadResponse?.error &&
                Object.keys(uploadResponse?.error)?.length > 0
              ) {
                setShowLoader(false);
                showToast(uploadResponse?.error?.message, "error");
                return;
              }
              setShowLoader(false);
              setIsUploading(false);
              sessionStorage.removeItem("updatedQuantities");
              sessionStorage.removeItem("trackingNumbers");
              sessionStorage.removeItem("ship_to_address");
              sessionStorage.removeItem("editedQuantity");
              setEditedQuantities({});
              setIsRefresh(!isRefresh);
              navigate(-1);
            }
          );
        }
      );
    });
  };

  const loadCarrierTracking = () => {
    if (carrierTrackingLoading || carrierTrackingLoaded) return;
    setCarrierTrackingLoading(true);
    const payload = { id: shipmentID };
    dispatch(getShipmentTrackingBYItemId(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setCarrierTrackingLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setCarrierTrackingLoading(false);
      setCarrierTrackingLoaded(true);
      setViewTrackingData(res?.payload || []);
    });
  };

  const canViewWmsSync = user.role === "MANAGER" || user.role === "SUPERADMIN";

  useEffect(() => {
    if (!canViewWmsSync || !shipmentID) return;
    setWmsSyncLoading(true);
    setWmsSyncError("");
    dispatch(syncShipmentWithWMS(shipmentID)).then((res) => {
      setWmsSyncLoading(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setWmsSyncError(res?.error?.message || "Could not sync with WMS. Please try again.");
        return;
      }
      setWmsSyncResults(res?.payload?.results || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentID]);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      tracking_number: value,
    }));
    sessionStorage.setItem("trackingNumbers", value);
  };

  const totalPages = Math.ceil((shipmentData?.box_details?.length || 0) / itemsPerPage);

  const shipmentItems = React.useMemo(
    () => getShipmentItems(shipmentData?.box_details),
    [shipmentData?.box_details]
  );

  const receivedItems = React.useMemo(
    () => getReceivedItems(wmsSyncResults || []),
    [wmsSyncResults]
  );

  const comparisonRows = React.useMemo(() => {
    const receivedByKey = new Map();
    receivedItems.forEach((item) => {
      const key = getReceivedComparisonKey(item);
      if (!key) return;
      const existing = receivedByKey.get(key) || {
        receivedQuantity: 0,
        cleanQuantity: 0,
        damagedQuantity: 0,
        trackingNumbers: new Set(),
        warehouseNames: new Set(),
        productName: item.product_name,
        identifierValue: item.identifier_value,
      };
      existing.receivedQuantity += item.receivedQuantity;
      existing.cleanQuantity += item.cleanQuantity;
      existing.damagedQuantity += item.damagedQuantity;
      existing.productName = existing.productName || item.product_name;
      existing.identifierValue = existing.identifierValue || item.identifier_value;
      if (item.trackingNumber) existing.trackingNumbers.add(item.trackingNumber);
      if (item.warehouseName) existing.warehouseNames.add(item.warehouseName);
      receivedByKey.set(key, existing);
    });

    const expectedKeys = new Set();
    const expectedRows = shipmentItems.map((item) => {
      const key = getExpectedComparisonKey(item);
      if (key) expectedKeys.add(key);
      const received = receivedByKey.get(key);
      const receivedQuantity = received?.receivedQuantity || 0;
      const cleanQuantity = received?.cleanQuantity || 0;
      const damagedQuantity = received?.damagedQuantity || 0;
      const difference = receivedQuantity - item.expectedQuantity;
      const status =
        !wmsSyncResults
          ? "Awaiting Sync"
          : receivedQuantity === 0
            ? "Not Received"
            : damagedQuantity > 0
              ? "Damaged"
              : difference === 0
                ? "Matched"
                : difference < 0
                  ? "Missing"
                  : "Over Received";

      return {
        ...item,
        receivedQuantity,
        cleanQuantity,
        damagedQuantity,
        difference,
        comparisonStatus: status,
        trackingNumbers: received ? Array.from(received.trackingNumbers) : [],
        warehouseNames: received ? Array.from(received.warehouseNames) : [],
      };
    });

    const warehouseOnlyRows = Array.from(receivedByKey.entries())
      .filter(([key]) => key && !expectedKeys.has(key))
      .map(([key, received]) => ({
        id: key,
        name: received.productName || "Warehouse-only item",
        identifierValue: received.identifierValue || key,
        boxName: "Not in member boxes",
        boxId: "wms-only",
        itemIndex: key,
        expectedQuantity: 0,
        receivedQuantity: received.receivedQuantity,
        cleanQuantity: received.cleanQuantity,
        damagedQuantity: received.damagedQuantity,
        difference: received.receivedQuantity,
        comparisonStatus: received.damagedQuantity > 0 ? "Damaged" : "Over Received",
        trackingNumbers: Array.from(received.trackingNumbers),
        warehouseNames: Array.from(received.warehouseNames),
      }));

    return [...expectedRows, ...warehouseOnlyRows];
  }, [shipmentItems, receivedItems, wmsSyncResults]);

  const comparisonSummary = React.useMemo(() => {
    const expected = shipmentItems.reduce((sum, item) => sum + item.expectedQuantity, 0);
    const received = receivedItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
    const clean = receivedItems.reduce((sum, item) => sum + item.cleanQuantity, 0);
    const damaged = receivedItems.reduce((sum, item) => sum + item.damagedQuantity, 0);
    const matched = comparisonRows.reduce(
      (sum, item) => sum + Math.min(item.expectedQuantity, item.receivedQuantity),
      0
    );
    const missing = comparisonRows.reduce(
      (sum, item) => sum + Math.max(item.expectedQuantity - item.receivedQuantity, 0),
      0
    );
    const over = comparisonRows.reduce(
      (sum, item) => sum + Math.max(item.receivedQuantity - item.expectedQuantity, 0),
      0
    );
    const issueRows = comparisonRows.filter((item) => item.comparisonStatus !== "Matched");
    const verdict =
      wmsSyncLoading
        ? "Checking warehouse receiving"
        : wmsSyncError
          ? "WMS sync needs attention"
          : !wmsSyncResults
            ? "Waiting for warehouse comparison"
            : wmsSyncResults.length === 0
              ? "No tracking numbers to sync"
              : issueRows.length === 0
                ? "Matched"
                : damaged > 0
                  ? "Damaged items found"
                  : missing > 0
                    ? "Missing units"
                    : over > 0
                      ? "Over received"
                      : "Partially received";

    return { expected, received, clean, damaged, matched, missing, over, issueRows, verdict };
  }, [shipmentItems, receivedItems, comparisonRows, wmsSyncResults, wmsSyncLoading, wmsSyncError]);

  const visibleComparisonRows = React.useMemo(() => {
    if (comparisonFilter === "issues") {
      return comparisonRows.filter((item) => item.comparisonStatus !== "Matched");
    }
    return comparisonRows;
  }, [comparisonRows, comparisonFilter]);

  const canUploadLabel = user.role !== "MEMBER" && shipmentData?.status === "LABEL_PENDING" && shipmentType !== "SHIP_DIRECT";
  const canUploadReceipt =
    user.role === "MEMBER" &&
    shipmentType !== "SHIP_DIRECT" &&
    !shipmentData?.is_receipt_uploaded &&
    shipmentData?.status !== "LABEL_PENDING" &&
    shipmentData?.status !== "CANCELLED";
  const canDownloadReceipt = Boolean(shipmentData?.is_receipt_uploaded);
  const canDownloadLabel = shipmentData?.status !== "LABEL_PENDING" && shipmentType !== "SHIP_DIRECT";
  const canViewTracking =
    shipmentData?.status === "SHIPPED" ||
    shipmentData?.status === "RECEIVED" ||
    shipmentData?.status === "IN_TRANSIT";
  const canViewWarehouseReceiving = canViewWmsSync;
  const showCarrierTrackingTab = false;
  const hasMoreActions = canUploadLabel || canUploadReceipt;
  const tabOptions = React.useMemo(() => [
    { key: "comparison", label: "Comparison" },
    { key: "boxes", label: "Boxes" },
    ...(showCarrierTrackingTab && canViewTracking ? [{ key: "carrier", label: "Carrier Tracking" }] : []),
    ...(canViewWarehouseReceiving ? [{ key: "warehouse", label: "Warehouse Receiving" }] : []),
    { key: "details", label: "Details" },
  ], [canViewTracking, canViewWarehouseReceiving, showCarrierTrackingTab]);

  const handleSelectTab = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey === "carrier") loadCarrierTracking();
  };

  useEffect(() => {
    if (!tabOptions.some((tab) => tab.key === activeTab)) {
      setActiveTab("comparison");
    }
  }, [activeTab, tabOptions]);

  const formatAddress = (addressObj) => {
    if (!addressObj) return "";
    const parts = [
      addressObj.address,
      addressObj.building,
      [addressObj.city, addressObj.state, addressObj.zipcode]
        .filter(Boolean)
        .join(", "),
    ].filter(Boolean);
    return parts.join(" , ");
  };

  const shipmentFields = [
    { label: "Shipping ID", value: shipmentData?.id, showkey: true },
    { label: "Tracking Number", value: shipmentData?.tracking_number, showkey: true },
    { label: "Total Boxes", value: shipmentData?.total_boxes, showkey: shipmentType !== "SHIP_DIRECT" },
    { label: "Total SKUs", value: shipmentData?.total_items, showkey: shipmentType !== "SHIP_DIRECT" },
    { label: "Total Quantity", value: shipmentData?.total_quantity, showkey: true },
    {
      label: "Total Weight",
      value: shipmentData?.id ? `${shipmentData?.total_weight_value} ${shipmentData?.total_weight_unit}` : null,
      showkey: shipmentType !== "SHIP_DIRECT",
    },
    {
      label: "Shipment Status",
      value: shipmentData?.status
        ? <StatusBadge status={shipmentData?.status} label={shipmentData?.displayStatus} />
        : null,
      showkey: true,
    },
    { label: "Member Name", value: shipmentData?.member_name, showkey: true },
    {
      label: "Shipment Creation Date",
      value: shipmentData?.created_at
        ? user?.role === "MEMBER"
          ? convertDateFormatInUtc(shipmentData?.created_at, "MMMM D, YYYY")
          : convertDateToCT(shipmentData?.created_at, "MMMM D, YYYY")
        : "NA",
      showkey: true,
    },
    {
      label: "Ship from Address",
      value: formatAddress({
        address: shipmentData?.address,
        building: shipmentData?.building,
        city: shipmentData?.city,
        zipcode: shipmentData?.zipcode,
        state: shipmentData?.state,
      }) || "NA",
      showkey: shipmentData?.address && shipmentType !== "SHIP_DIRECT",
    },
    { label: "Ship to Address", value: shipmentData?.ship_to_address || "NA", showkey: shipmentData?.ship_to_address },
    { label: "Phone Number", value: shipmentData?.user_details?.phone_numbers || "NA", showkey: true },
    {
      label: "Shipment Date",
      value: shipmentData?.shipment_date
        ? convertDateFormatInUtc(shipmentData?.shipment_date, "MMMM D, YYYY")
        : null,
      showkey: true,
    },
    { label: "Shipment Type", value: shipmentData?.shipment_type?.replace("_", " ") || "NA", showkey: true },
  ];

  const shipmentDetailSections = [
    {
      title: "Shipment Information",
      labels: ["Shipping ID", "Tracking Number", "Shipment Status", "Shipment Creation Date", "Shipment Date", "Shipment Type"],
    },
    {
      title: "Operational Information",
      labels: ["Total Boxes", "Total SKUs", "Total Quantity", "Total Weight"],
    },
    {
      title: "Member Information",
      labels: ["Member Name", "Phone Number"],
    },
    {
      title: "Addresses",
      labels: ["Ship from Address", "Ship to Address"],
    },
  ].map((section) => ({
    ...section,
    fields: shipmentFields.filter((field) => section.labels.includes(field.label) && field.value && field.showkey),
  })).filter((section) => section.fields.length > 0);

  // ─── Status dialog info panel ────────────────────────────────────────────────
  const StatusInfoPanel = ({ status }) => {
    if (!status) return null;
    const configs = {
      CANCELLED: {
        borderColor: "#F44336",
        bg: "rgba(244,67,54,0.1)",
        titleColor: "#F44336",
        title: "Important Notice",
        body: "Cancelling this shipment will permanently stop any further processing. Any corresponding payouts will also be cancelled. This action cannot be reversed.",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        ),
      },
      IN_TRANSIT: {
        borderColor: "#2196F3",
        bg: "rgba(33,150,243,0.1)",
        titleColor: "#2196F3",
        title: "Shipment Update",
        body: 'Marking this shipment as "In Transit" indicates that the package is currently being transported to its destination. Tracking information will be monitored for delivery progress.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        ),
      },
      RECEIVED: {
        borderColor: "#4CAF50",
        bg: "rgba(76,175,80,0.15)",
        titleColor: "#4CAF50",
        title: "Delivery Confirmation",
        body: 'Marking this shipment as "Received" confirms that all items have arrived at the destination. This will trigger the payment process according to the terms outlined in the member agreement.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ),
      },
    };
    const cfg = configs[status] || {
      borderColor: "rgba(255,255,255,0.12)",
      bg: "rgba(255,255,255,0.08)",
      titleColor: "#fff",
      title: "Status Change",
      body: "A payout will be created for the order. This action will be recorded, and notifications may be sent to relevant parties.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      ),
    };

    return (
      <div style={{
        marginTop: 16,
        padding: "12px 14px",
        borderRadius: 6,
        backgroundColor: cfg.bg,
        borderLeft: `4px solid ${cfg.borderColor}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
      }}>
        <div style={{ color: cfg.borderColor, marginTop: 2, flexShrink: 0 }}>{cfg.icon}</div>
        <div>
          <p style={{ fontWeight: 600, color: cfg.titleColor, margin: "0 0 4px", fontSize: 13 }}>{cfg.title}</p>
          <p style={{ color: "#8b9296", margin: 0, fontSize: 13 }}>{cfg.body}</p>
        </div>
      </div>
    );
  };

  const renderBoxAccordionContent = (item, editable) => (
    <div style={{ paddingTop: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: editable ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(100px, 1fr))", gap: 8, padding: "0 12px 4px" }}>
        <div style={{ minWidth: 0, padding: "9px 10px", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, background: "rgba(255,255,255,0.025)" }}>
          <div style={{ color: "#8b9296", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>SKUs</div>
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginTop: 3 }}>{item?.box_items?.length ?? 0}</div>
        </div>
        <div style={{ minWidth: 0, padding: "9px 10px", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, background: "rgba(255,255,255,0.025)" }}>
          <div style={{ color: "#8b9296", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>Weight</div>
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginTop: 3, overflowWrap: "anywhere" }}>{item?.box_dimension?.weight || "—"} <span style={{ color: "#8b9296", fontSize: 11, fontWeight: 600 }}>lbs</span></div>
        </div>
        {!editable && item?.box_dimension && (
          <div style={{ minWidth: 0, padding: "9px 10px", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, background: "rgba(255,255,255,0.025)" }}>
            <div style={{ color: "#8b9296", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>Dimensions</div>
            <div style={{ color: "#fff", fontSize: 13, fontWeight: 800, marginTop: 4, overflowWrap: "anywhere" }}>
              {item?.box_dimension?.length} X {item?.box_dimension?.width} X {item?.box_dimension?.height} <span style={{ color: "#8b9296", fontSize: 11, fontWeight: 600 }}>in</span>
            </div>
          </div>
        )}
      </div>
      <div style={{ paddingLeft: 8 }}>
        <Slider
          {...settings}
          ref={(slider) => { sliderRefs.current[item.id] = slider; }}
          afterChange={(current) => settings.afterChange(current, item.id)}
        >
          {item?.box_items?.map?.((boxItem, index) => (
            <div key={index}>
              {editable
                ? <EditableItemCard item={{ ...boxItem, index }} boxId={item.id} />
                : <ItemCard item={{ ...boxItem, index }} />
              }
            </div>
          ))}
        </Slider>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 8, padding: "6px 4px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => previous(item.id)}
            disabled={slideIndexes[item.id] === 0 || item?.box_items?.length <= 1}
            style={{ opacity: (slideIndexes[item.id] === 0 || item?.box_items?.length <= 1) ? 0.4 : 1 }}
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => next(item.id)}
            disabled={slideIndexes[item.id] === item?.box_items?.length - 1 || item?.box_items?.length <= 1}
            style={{ opacity: (slideIndexes[item.id] === item?.box_items?.length - 1 || item?.box_items?.length <= 1) ? 0.4 : 1 }}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderBoxList = () => (
    <>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: isMobile ? 8 : 12 }}>
        {shipmentData?.box_details
          ?.slice(indexOfFirstItem, indexOfLastItem)
          .map((item) => (
          <Collapsible
            key={item.id}
            open={openBoxes[item.id] || false}
            onOpenChange={(v) => setOpenBoxes((prev) => ({ ...prev, [item.id]: v }))}
            style={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 10,
              background: "rgba(255,255,255,0.02)",
              overflow: "hidden",
            }}
          >
            <CollapsibleTrigger
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "13px 14px",
                background: "transparent",
                border: "none",
                borderRadius: 0,
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
              }}
            >
              <span style={{ minWidth: 0, overflowWrap: "anywhere", fontWeight: 800 }}>{item.box_name}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "#8b9296", fontSize: 12 }}>
                {(item.box_items || []).reduce((sum, boxItem) => sum + toNumber(boxItem.quantity), 0)} units
                <ChevronDown
                  size={15}
                  style={{
                    transition: "transform 0.2s",
                    transform: openBoxes[item.id] ? "rotate(180deg)" : "none",
                    color: "#8b9296",
                  }}
                />
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div style={{ padding: "0 0 14px" }}>
                {renderBoxAccordionContent(item, shipmentType === "SHIP_DIRECT")}
              </div>
            </CollapsibleContent>
          </Collapsible>
          ))}
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 4 }}>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
          >
            <ChevronLeft size={16} />
          </Button>
          <span style={{ color: "#8b9296", fontSize: 13 }}>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </>
  );

  const renderMemberShipDirectTools = () => (
    user.role === "MEMBER" && shipmentData?.status === "PENDING_SHIPMENT" && (
      <>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
          <Button
            variant="outline"
            onClick={handleResetQuantities}
            disabled={Object.keys(editedQuantities).length === 0}
          >
            Reset
          </Button>
        </div>

        <hr style={{ borderColor: "rgba(255,255,255,0.08)", margin: "8px 0" }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: "8px 0 14px" }}>Bulk Tracking Import</h2>

        <div>
          <Label htmlFor="shipping_address" style={{ fontSize: 11, letterSpacing: "0.8px", color: "#c3c8cc", marginBottom: 6, display: "block", textTransform: "uppercase" }}>
            Ship to Address
          </Label>
          <Select
            id="shipping_address"
            value={formData.ship_to_address}
            onChange={(e) => handleAddressSelect(e.target.value)}
          >
            <SelectOption value="">Select the Address</SelectOption>
            {shipping_address.map((addr) => (
              <SelectOption key={addr} value={addr}>RSC - {addr}</SelectOption>
            ))}
          </Select>
        </div>

        <div style={{ marginTop: 12 }}>
          <Label htmlFor="tracking_number" style={{ fontSize: 11, letterSpacing: "0.8px", color: "#c3c8cc", marginBottom: 6, display: "block", textTransform: "uppercase" }}>
            Enter tracking Number. You can enter multiple numbers separated by commas, spaces, or new lines.
          </Label>
          <Textarea
            id="tracking_number"
            rows={10}
            value={formData.tracking_number}
            onChange={handleInputChange}
            placeholder="Enter tracking IDs separated by commas, spaces, or new lines"
            style={{ minHeight: 200 }}
          />
          {errorMessage && (
            <p style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>{errorMessage}</p>
          )}
        </div>

        <Button
          disabled={boxItem?.length === 0 || !formData.ship_to_address || !formData.tracking_number}
          onClick={handleSaveShipment}
          style={{ marginTop: 10 }}
        >
          Save
        </Button>
      </>
    )
  );

  return (
    <>
      {showLoader && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)" }}>
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>Loading...</div>
        </div>
      )}

      <div style={{ width: "100%", minWidth: 0, boxSizing: "border-box", display: "flex", justifyContent: "center", margin: "0 auto", padding: isMobile ? "12px 12px 40px" : "20px 20px 48px", overflowX: "hidden" }}>
        <div style={{ width: "100%", minWidth: 0, maxWidth: 1320, margin: "0 auto" }}>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => navigate(-1)}
            style={{
              gap: 6,
              color: "rgba(255,255,255,0.52)",
              marginBottom: 14,
              paddingLeft: 0,
            }}
          >
            <ArrowLeft size={16} /> Back
          </Button>

          <div
            style={{
              marginBottom: 14,
              padding: isMobile ? 12 : 16,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              background: "rgba(255,255,255,0.025)",
            }}
          >
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h1 style={{ fontSize: isMobile ? 20 : 30, fontWeight: 800, color: "#fff", margin: 0 }}>{isMobile ? `#${shipmentData?.id || "—"}` : `Shipment #${shipmentData?.id || "—"}`}</h1>
                  {isMobile ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, flexWrap: "wrap" }}>
                      {shipmentData?.status && <StatusBadge status={shipmentData?.status} label={shipmentData?.displayStatus} />}
                      {shipmentData?.shipment_type && (
                        <span style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "2px 7px", fontSize: 10, fontWeight: 700, color: "#8b9296", whiteSpace: "nowrap" }}>
                          {shipmentData?.shipment_type?.replace(/_/g, " ")}
                        </span>
                      )}
                      <span style={{ color: "#8b9296", fontSize: 11 }}>{shipmentData?.member_name || "Unknown"}</span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                      {shipmentData?.status && <StatusBadge status={shipmentData?.status} label={shipmentData?.displayStatus} />}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center", flexWrap: isMobile ? "nowrap" : "wrap" }}>
                  {shipmentData?.status && user.role !== "MEMBER" && (
                    <Button size={isMobile ? "sm" : "default"} onClick={() => setOpenStatusDialog(true)}>{isMobile ? "Edit" : "Edit Status"}</Button>
                  )}
                  {!isMobile && canDownloadReceipt && (
                    <Button variant="outline" onClick={() => handleDownloadShipmentDoc("RECEIPT")}>
                      Download Receipt
                    </Button>
                  )}
                  {!isMobile && canDownloadLabel && (
                    <Button variant="outline" onClick={() => handleDownloadShipmentDoc("LABEL")}>
                      Download Label
                    </Button>
                  )}
                  {(hasMoreActions || (isMobile && (canDownloadReceipt || canDownloadLabel))) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" title="More actions">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" style={{ minWidth: 210 }}>
                        {isMobile && canDownloadReceipt && (
                          <DropdownMenuItem onClick={() => handleDownloadShipmentDoc("RECEIPT")}>
                            Download Receipt
                          </DropdownMenuItem>
                        )}
                        {isMobile && canDownloadLabel && (
                          <DropdownMenuItem onClick={() => handleDownloadShipmentDoc("LABEL")}>
                            Download Label
                          </DropdownMenuItem>
                        )}
                        {canUploadLabel && (
                          <DropdownMenuItem onClick={() => navigate("/dashboard/shipment/upload-shipment-label", { state: { shipmentId: shipmentData?.id } })}>
                            Upload Label
                          </DropdownMenuItem>
                        )}
                        {canUploadReceipt && (
                          <DropdownMenuItem onClick={() => navigate("/dashboard/shipment/upload-shipment-receipt", { state: { shipmentId: shipmentData?.id } })}>
                            Upload Receipt
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              {!isMobile && (
                <p style={{ color: "#8b9296", margin: "6px 0 0", fontSize: 13, overflowWrap: "anywhere" }}>
                  {shipmentData?.member_name || "Unknown member"} · {shipmentData?.shipment_type?.replace(/_/g, " ") || "NA"}
                  {shipmentData?.tracking_number ? ` · ${shipmentData.tracking_number}` : ""}
                </p>
              )}
            </div>

            <div
              style={{
                marginTop: 18,
                padding: isMobile ? "14px 0 2px" : "16px 0 4px",
                borderTop: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      color: comparisonSummary.issueRows.length ? "#ff8a75" : "#65e08a",
                      fontSize: 14,
                      fontWeight: 800,
                    }}
                  >
                    {comparisonSummary.issueRows.length > 0 && <AlertTriangle size={16} />}
                    {comparisonSummary.verdict}
                  </div>
                  <p style={{ color: "#c3c8cc", margin: "6px 0 0", fontSize: 13 }}>
                    Expected {comparisonSummary.expected}, received {comparisonSummary.received}.
                    {comparisonSummary.missing > 0 ? ` ${comparisonSummary.missing} missing.` : ""}
                    {comparisonSummary.over > 0 ? ` ${comparisonSummary.over} over received.` : ""}
                    {comparisonSummary.damaged > 0 ? ` ${comparisonSummary.damaged} damaged.` : ""}
                  </p>
                </div>
                {canViewWmsSync && wmsSyncLoading && (
                  <span style={{ color: "#8b9296", fontSize: 12.5 }}>Checking WMS...</span>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: isMobile ? 10 : 12 }}>
                {[
                  { label: "Expected", value: comparisonSummary.expected, color: "#fff" },
                  { label: "Received", value: comparisonSummary.received, color: "#fff" },
                  { label: "Damaged", value: comparisonSummary.damaged, color: comparisonSummary.damaged ? "#ffb86b" : "#fff" },
                  { label: "Issues", value: comparisonSummary.issueRows.length, color: comparisonSummary.issueRows.length ? "#ffb86b" : "#fff" },
                ].map((item) => (
                  <div key={item.label} style={{ minWidth: 0, padding: "10px 12px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, background: "rgba(255,255,255,0.025)" }}>
                    <div style={{ color: "#8b9296", fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>{item.label}</div>
                    <div style={{ color: item.color, fontSize: 24, fontWeight: 800, marginTop: 3 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(260px, 300px)",
              alignItems: "start",
              gap: isMobile ? 14 : 18,
            }}
          >
          <main style={{ width: "100%", minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              gap: 8,
              marginBottom: 12,
              padding: 4,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12,
              background: "rgba(255,255,255,0.025)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              position: "sticky",
              top: 8,
              zIndex: 5,
            }}
          >
            {tabOptions.map((tab) => {
              const active = activeTab === tab.key;
              return (
                <Button
                  key={tab.key}
                  type="button"
                  variant={active ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleSelectTab(tab.key)}
                  style={{
                    borderRadius: 8,
                    color: active ? "#000" : "#c3c8cc",
                    flexShrink: 0,
                    height: 32,
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </div>

          <div
            style={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              background: "rgba(255,255,255,0.018)",
              padding: isMobile ? 14 : 20,
            }}
          >
          {activeTab === "comparison" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0 }}>Expected vs Received</h2>
                  <p style={{ color: "#b8c1c8", fontSize: 12.5, margin: "3px 0 0" }}>Member shipment quantities compared with warehouse receiving quantities.</p>
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: 3,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.045)",
                  }}
                >
                  {[
                    { key: "all", label: "All" },
                    { key: "issues", label: "Issues" },
                  ].map((filter) => {
                    const active = comparisonFilter === filter.key;
                    return (
                      <Button
                        key={filter.key}
                        variant="ghost"
                        size="sm"
                        onClick={() => setComparisonFilter(filter.key)}
                        style={{
                          borderRadius: 999,
                          background: active ? "#00ADB5" : "transparent",
                          color: active ? "#000" : "#d9e1e7",
                          height: 28,
                          padding: "0 12px",
                          fontWeight: 700,
                        }}
                      >
                        {filter.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {visibleComparisonRows.length > 0 ? (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "100%" : "320px"}, 1fr))`, gap: isMobile ? 10 : 12 }}>
                  {visibleComparisonRows.map((item, index) => {
                    const issue = item.comparisonStatus !== "Matched";
                    const statusStyle = getComparisonStatusStyle(item.comparisonStatus);
                    return (
                      <div
                        key={`${item.boxId}-${item.itemIndex}-${index}`}
                        style={{
                          border: "1px solid rgba(255,255,255,0.09)",
                          borderLeft: `3px solid ${issue ? statusStyle.color : "#65e08a"}`,
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.035)",
                          padding: isMobile ? 12 : 16,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: "#fff", fontSize: 14, fontWeight: 800, overflowWrap: "anywhere" }}>{item.name || "NA"}</div>
                            <div style={{ color: "#b8c1c8", fontSize: 12.5, marginTop: 4, overflowWrap: "anywhere" }}>
                              {getComparisonMeta(item)} · {item.boxName || "NA"}
                            </div>
                          </div>
                          <span
                            style={{
                              ...statusStyle,
                              display: "inline-flex",
                              alignItems: "center",
                              border: `1px solid ${statusStyle.borderColor}`,
                              borderRadius: 999,
                              padding: "3px 8px",
                              fontSize: 12,
                              fontWeight: 800,
                              lineHeight: 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.comparisonStatus}
                          </span>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10, marginTop: 14 }}>
                          {[
                            { label: "Expected", value: item.expectedQuantity, color: "#fff" },
                            { label: "Received", value: item.receivedQuantity, color: "#fff" },
                            { label: "Damaged", value: item.damagedQuantity, color: item.damagedQuantity ? "#ff8a75" : "#b8c1c8" },
                            { label: "Diff", value: formatDelta(item.difference), color: issue ? "#ff8a75" : "#65e08a" },
                          ].map((metric) => (
                            <div key={metric.label}>
                              <div style={{ color: "#b8c1c8", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" }}>{metric.label}</div>
                              <div style={{ color: metric.color, fontSize: 18, fontWeight: 800, marginTop: 3 }}>{metric.value}</div>
                            </div>
                          ))}
                        </div>

                        {(item.trackingNumbers?.length > 0 || item.warehouseNames?.length > 0) && (
                          <div style={{ color: "#b8c1c8", fontSize: 12, marginTop: 12, overflowWrap: "anywhere" }}>
                            {item.warehouseNames?.length > 0 ? item.warehouseNames.join(", ") : "Warehouse"} · {item.trackingNumbers?.join(", ") || "No tracking match"}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: 24, color: "#8b9296", textAlign: "center", fontSize: 13, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}>
                  No comparison rows to show.
                </div>
              )}
            </div>
          )}

          {activeTab === "boxes" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0 }}>Boxes</h2>
                <p style={{ color: "#b8c1c8", fontSize: 12.5, margin: "3px 0 0" }}>Original member box details, quantities, dimensions, and item cards.</p>
              </div>
              {renderBoxList()}
              {shipmentType === "SHIP_DIRECT" && renderMemberShipDirectTools()}
            </div>
          )}

          {activeTab === "carrier" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0 }}>Carrier Tracking</h2>
                <p style={{ color: "#b8c1c8", fontSize: 12.5, margin: "3px 0 0" }}>Carrier status and external tracking links for this shipment.</p>
              </div>
              {carrierTrackingLoading ? (
                <div style={{ color: "#b8c1c8", fontSize: 13, padding: "14px 0" }}>
                  Loading carrier tracking...
                </div>
              ) : viewTrackingData?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {viewTrackingData.map((tracking, index) => (
                    <div key={index} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "14px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: "#fff", fontSize: 13.5, fontWeight: 800 }}>{tracking.carrier_name || "Unknown Carrier"}</div>
                          <div style={{ color: "#b8c1c8", fontSize: 12.5, marginTop: 4, overflowWrap: "anywhere" }}>{tracking.carrier_tracking_id || "NA"}</div>
                          {tracking.last_update && (
                            <div style={{ color: "#b8c1c8", fontSize: 12, marginTop: 8 }}>Last update: {tracking.last_update}</div>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Badge variant="info" style={{ color: "#7dd3fc", background: "rgba(125,211,252,0.12)", border: "1px solid rgba(125,211,252,0.32)" }}>{tracking.status || "Pending"}</Badge>
                          {tracking.tracking_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(tracking.tracking_url, "_blank")}
                            >
                              Track
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#b8c1c8", fontSize: 13, padding: "14px 0", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  No carrier tracking information found.
                </div>
              )}
            </div>
          )}

          {activeTab === "warehouse" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0 }}>Warehouse Receiving</h2>
                <p style={{ color: "#b8c1c8", fontSize: 12.5, margin: "3px 0 0" }}>WMS receiving totals, received products, and damage photos by tracking number.</p>
              </div>
              {canViewWmsSync && wmsSyncLoading && (
                <div style={{ color: "#b8c1c8", fontSize: 13, padding: "14px 0" }}>
                  Checking WMS receiving status...
                </div>
              )}
              {canViewWmsSync && wmsSyncError && (
                <div style={{ color: "#ff8a75", fontSize: 13, padding: "14px 0", borderTop: "1px solid rgba(255,138,117,0.24)" }}>
                  {wmsSyncError}
                </div>
              )}
              {canViewWmsSync && !wmsSyncLoading && !wmsSyncError && wmsSyncResults?.length > 0 && (
                <Accordion
                  type="single"
                  collapsible
                  defaultValue={`tracking-${wmsSyncResults[0]?.tracking_number || 0}`}
                  style={{ display: "flex", flexDirection: "column", gap: 0 }}
                >
                  {wmsSyncResults.map((result, index) => {
                    const { wrapperStatus, label } = mapWmsStatus(result.status);
                    const hasDetail = result.status !== "not_received_yet" && result.status !== "wms_sync_failed";
                    const damagedUnits = (result.units || []).filter((unit) => unit.is_damaged);

                    return (
                      <AccordionItem
                        key={index}
                        value={`tracking-${result.tracking_number || index}`}
                        style={{ border: "none", borderBottom: "1px solid rgba(255,255,255,0.09)", borderRadius: 0, background: "transparent", overflow: "hidden" }}
                      >
                        <AccordionTrigger
                          style={{ padding: "14px 0", background: "transparent", border: "none", color: "#fff", fontFamily: "inherit", cursor: "pointer" }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, width: "100%", minWidth: 0 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: "#fff", fontSize: 13.5, fontWeight: 800, overflowWrap: "anywhere" }}>{result.tracking_number}</div>
                              <div style={{ color: "#b8c1c8", fontSize: 12.5, marginTop: 4 }}>{result.warehouse_name || "No warehouse yet"}</div>
                              {isMobile && hasDetail && (
                                <div style={{ color: "#d9e1e7", fontSize: 12, marginTop: 4 }}>
                                  {result.total_units ?? 0} received · {result.clean_units ?? 0} clean · {result.damaged_units ?? 0} damaged
                                </div>
                              )}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              {!isMobile && hasDetail && (
                                <span style={{ color: "#d9e1e7", fontSize: 12, whiteSpace: "nowrap" }}>
                                  {result.total_units ?? 0} received · {result.clean_units ?? 0} clean · {result.damaged_units ?? 0} damaged
                                </span>
                              )}
                              <StatusBadge status={wrapperStatus} label={label} />
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent style={{ padding: "0 0 16px" }}>
                          {hasDetail ? (
                            <>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 10, marginBottom: result.items?.length ? 12 : 0 }}>
                                {[
                                  { label: "Received", value: result.total_units ?? "NA" },
                                  { label: "Clean", value: result.clean_units ?? "NA" },
                                  { label: "Damaged", value: result.damaged_units ?? "NA" },
                                ].map((stat) => (
                                  <div key={stat.label}>
                                    <div style={{ color: "#b8c1c8", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" }}>{stat.label}</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 800, marginTop: 3 }}>{stat.value}</div>
                                  </div>
                                ))}
                              </div>

                              {result.items?.length > 0 && (
                                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
                                  <div style={{ color: "#b8c1c8", fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Products Received</div>
                                  {result.items.map((item, itemIndex) => (
                                    <div key={itemIndex} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", gap: isMobile ? 2 : 10, marginBottom: 8 }}>
                                      <span style={{ fontSize: 12.5, color: "#fff", overflowWrap: "anywhere" }}>{item.product_name || item.identifier_value || "Received item"}</span>
                                      <span style={{ fontSize: 12.5, color: "#d9e1e7", whiteSpace: "nowrap" }}>{item.clean_quantity} clean / {item.damaged_quantity} damaged</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {damagedUnits.length > 0 && (
                                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10, marginTop: 10 }}>
                                  <div style={{ color: "#ff8a75", fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Damage Photos</div>
                                  {damagedUnits.map((unit, unitIndex) => (
                                    <div key={unitIndex} style={{ marginTop: unitIndex === 0 ? 0 : 12 }}>
                                      <div style={{ color: "#fff", fontSize: 12.5, fontWeight: 700 }}>{unit.product_name || unit.identifier_value || "Damaged item"}</div>
                                      <div style={{ color: "#ff8a75", fontSize: 12, marginTop: 2 }}>
                                        {(unit.damage_reason || "Damaged").replaceAll("_", " ")}
                                        {unit.damage_notes ? ` - ${unit.damage_notes}` : ""}
                                      </div>
                                      {unit.damage_image_urls?.length > 0 && (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                                          {unit.damage_image_urls.map((url, urlIndex) => (
                                            <AuthenticatedWmsImage
                                              key={urlIndex}
                                              path={url}
                                              alt={`${unit.product_name || "Item"} damage photo ${urlIndex + 1}`}
                                              onClick={() => setLightboxPhoto(url)}
                                              style={{ width: 72, height: 72, borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)" }}
                                            />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ color: "#b8c1c8", fontSize: 13 }}>
                              {result.status === "wms_sync_failed"
                                ? "Could not reach WMS for this tracking number."
                                : "Not received at the warehouse yet."}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
              {canViewWmsSync && !wmsSyncLoading && !wmsSyncError && wmsSyncResults?.length === 0 && (
                <div style={{ padding: isMobile ? 16 : 20, border: "1px dashed rgba(255,255,255,0.14)", borderRadius: 10, background: "rgba(255,255,255,0.02)" }}>
                  <div style={{ color: "#fff", fontSize: 13.5, fontWeight: 800 }}>No warehouse receiving data yet</div>
                  <div style={{ color: "#8b9296", fontSize: 12.5, marginTop: 5 }}>Receiving information will appear after tracking and warehouse data becomes available.</div>
                </div>
              )}
              {!canViewWmsSync && (
                <div style={{ color: "#b8c1c8", fontSize: 13, padding: "14px 0", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  Warehouse receiving details are available to managers and admins.
                </div>
              )}
            </div>
          )}

          {activeTab === "details" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0 }}>Details</h2>
                <p style={{ color: "#b8c1c8", fontSize: 12.5, margin: "3px 0 0" }}>Shipment metadata and addresses from the original detail view.</p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: isMobile ? 14 : 16 }}>
                {shipmentDetailSections.map((section) => (
                  <section
                    key={section.title}
                    style={{
                      minWidth: 0,
                      padding: isMobile ? 12 : 14,
                      border: "1px solid rgba(255,255,255,0.07)",
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.02)",
                    }}
                  >
                    <h3 style={{ color: "#fff", fontSize: 12.5, fontWeight: 800, margin: "0 0 4px" }}>{section.title}</h3>
                    {section.fields.map((item) => (
                      <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.07)", flexDirection: isMobile ? "column" : "row" }}>
                        <span style={{ fontSize: 12.5, color: "#b8c1c8" }}>{item.label}</span>
                        <span style={{ fontSize: 12.5, color: "#fff", textAlign: isMobile ? "left" : "right", overflowWrap: "anywhere" }}>
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </div>
          )}
          </div>
          </main>
          <aside
            style={{
              position: isMobile ? "static" : "sticky",
              top: 16,
              minWidth: 0,
              padding: isMobile ? 14 : 16,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              background: "rgba(255,255,255,0.025)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
              <div>
                <h2 style={{ color: "#fff", fontSize: 14, fontWeight: 800, margin: 0 }}>Shipment Overview</h2>
                <p style={{ color: "#8b9296", fontSize: 11.5, margin: "4px 0 0" }}>Operational snapshot</p>
              </div>
              {shipmentData?.status && <StatusBadge status={shipmentData.status} label={shipmentData.displayStatus} />}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: "Shipment ID", value: shipmentData?.id || "NA" },
                { label: "Shipment Type", value: shipmentData?.shipment_type?.replace(/_/g, " ") || "NA" },
                { label: "Member / Vendor", value: shipmentData?.member_name || "Unknown" },
                { label: "Created", value: shipmentData?.createdAt || "NA" },
                { label: "Tracking", value: shipmentData?.tracking_number || "No tracking number" },
              ].map((field) => (
                <div key={field.label} style={{ minWidth: 0 }}>
                  <div style={{ color: "#8b9296", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" }}>{field.label}</div>
                  <div style={{ color: "#fff", fontSize: 12.5, marginTop: 3, overflowWrap: "anywhere" }}>{field.value}</div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 16, paddingTop: 14 }}>
              <div style={{ color: "#fff", fontSize: 12.5, fontWeight: 800, marginBottom: 10 }}>Operational Health</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                {[
                  { label: "Expected", value: comparisonSummary.expected, color: "#fff" },
                  { label: "Received", value: comparisonSummary.received, color: "#65e08a" },
                  { label: "Damaged", value: comparisonSummary.damaged, color: comparisonSummary.damaged ? "#ff8a75" : "#c3c8cc" },
                  { label: "Issues", value: comparisonSummary.issueRows.length, color: comparisonSummary.issueRows.length ? "#ffb86b" : "#65e08a" },
                ].map((metric) => (
                  <div key={metric.label} style={{ padding: "9px 10px", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, background: "rgba(255,255,255,0.02)" }}>
                    <div style={{ color: "#8b9296", fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>{metric.label}</div>
                    <div style={{ color: metric.color, fontSize: 19, fontWeight: 800, marginTop: 3 }}>{metric.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
          </div>
        </div>
      </div>

      {/* Update Status dialog */}
      <Dialog open={openStatusDialog} onOpenChange={(v) => !v && setOpenStatusDialog(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Shipment Status</DialogTitle>
          </DialogHeader>
          <div style={{ marginTop: 8 }}>
            <Label htmlFor="new-status" style={{ fontSize: 11, letterSpacing: "0.8px", color: "#c3c8cc", marginBottom: 6, display: "block", textTransform: "uppercase" }}>
              New Status
            </Label>
            <Select
              id="new-status"
              value={formData.status}
              onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
            >
              <SelectOption value="">Select new status</SelectOption>
              {getValidStatusTransitions(shipmentData.status).map((s) => (
                <SelectOption key={s} value={s}>
                  {s.split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")}
                </SelectOption>
              ))}
            </Select>
            <p style={{ marginTop: 8, fontSize: 13, color: "#8b9296" }}>
              Current Status: {shipmentData.displayStatus}
            </p>
            {formData.status && <StatusInfoPanel status={formData.status} />}
          </div>
          <DialogFooter style={{ marginTop: 20, gap: 8 }}>
            <Button variant="outline" onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={!formData.status}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Quantity dialog */}
      <Dialog open={updateQuantiyOpen} onOpenChange={(v) => !v && setUpdateQuantiyOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Quantity</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Specify the new quantity for this item and click 'Update' to save changes.
          </DialogDescription>
          <div style={{ marginTop: 12 }}>
            <Label htmlFor="qty-input" style={{ fontSize: 11, letterSpacing: "0.8px", color: "#c3c8cc", marginBottom: 6, display: "block", textTransform: "uppercase" }}>
              Quantity
            </Label>
            <Input
              id="qty-input"
              type="number"
              name="tempQuantity"
              value={tempQuantity}
              placeholder="Enter Quantity"
              ref={quantityInputRef}
              autoFocus
              onChange={(e) => {
                setTempQuantity(e.target.value);
                handleQuantityChange(e);
              }}
              onWheel={(e) => e.target.blur()}
              style={{
                MozAppearance: "textfield",
              }}
            />
            {quantityError && (
              <p style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>{quantityError}</p>
            )}
          </div>
          <DialogFooter style={{ marginTop: 16, gap: 8 }}>
            <Button
              variant="outline"
              onClick={() => { setUpdateQuantiyOpen(false); setQuantityError(false); }}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmUpdateQuantity} disabled={!!quantityError}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Damage photo lightbox */}
      <Dialog open={Boolean(lightboxPhoto)} onOpenChange={(v) => !v && setLightboxPhoto(null)}>
        <DialogContent showCloseButton={false} style={{ padding: 0, maxWidth: "85vw", maxHeight: "85vh", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button
            onClick={() => setLightboxPhoto(null)}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              color: "#fff",
              background: "rgba(0,0,0,0.5)",
              border: "none",
              borderRadius: 6,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <CloseX size={16} />
          </button>
          {lightboxPhoto && (
            <AuthenticatedWmsImage
              path={lightboxPhoto}
              alt="Damage photo"
              style={{
                maxWidth: "85vw",
                maxHeight: "85vh",
                minWidth: 300,
                minHeight: 300,
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewShipmentDetail;
